/**
 * Stop-loss “protect per investment” flow strings.
 * When you change these keys in `en.js`, update every language here and keep
 * locales importing `panelStopLossFlowByLang`.
 */

const flowKeys = {
  emptyDescNoSlots: 'user.stopLoss.emptyDescNoSlots',
  purchaseToastOk: 'user.stopLoss.purchase.toastOk',
  purchasePlanLine: 'user.stopLoss.purchase.planLine',
  protectTitle: 'user.stopLoss.protect.title',
  protectSubtitle: 'user.stopLoss.protect.subtitle',
  protectNoInvestments: 'user.stopLoss.protect.noInvestments',
  protectNoInvestmentsCta: 'user.stopLoss.protect.noInvestmentsCta',
  protectAllProtected: 'user.stopLoss.protect.allProtected',
  protectLoading: 'user.stopLoss.protect.loading',
  protectPlanMeta: 'user.stopLoss.protect.planMeta',
  protectFeeLine: 'user.stopLoss.protect.feeLine',
  protectPremiumLine: 'user.stopLoss.protect.premiumLine',
  protectCta: 'user.stopLoss.protect.cta',
  protectCtaChoosePlan: 'user.stopLoss.protect.ctaChoosePlan',
  protectModalTitle: 'user.stopLoss.protect.modalTitle',
  protectSelectPlanHint: 'user.stopLoss.protect.selectPlanHint',
  protectConfirm: 'user.stopLoss.protect.confirm',
  protectProcessing: 'user.stopLoss.protect.processing',
  protectToastOk: 'user.stopLoss.protect.toastOk',
  protectChoosePackage: 'user.stopLoss.protect.choosePackage',
  protectPlanTierLine: 'user.stopLoss.protect.planTierLine',
  protectForTierOnly: 'user.stopLoss.protect.forTierOnly',
  protectRecommendedTier: 'user.stopLoss.protect.recommendedTier',
  protectContinueWithPackage: 'user.stopLoss.protect.continueWithPackage',
  protectPackageSelected: 'user.stopLoss.protect.packageSelected',
  protectSetLimitsFor: 'user.stopLoss.protect.setLimitsFor',
  tablePlanProtection: 'user.stopLoss.table.planProtection',
  explainerBadgeMulti: 'user.stopLoss.explainer.badge.multi',
  howStep1Title: 'user.stopLoss.how.step1Title',
  howStep1Desc: 'user.stopLoss.how.step1Desc',
  investmentsStopLossApplied: 'user.investments.stopLossApplied',
};

export const panelStopLossFlowByLang = {
  es: {
    [flowKeys.emptyDescNoSlots]:
      'Protege una inversión activa abajo (un stop-loss por plan).',
    [flowKeys.purchaseToastOk]: 'Protección stop-loss aplicada',
    [flowKeys.purchasePlanLine]: 'Plan: {{name}}',
    [flowKeys.protectTitle]: 'Proteger una inversión',
    [flowKeys.protectSubtitle]:
      'Cada plan activo necesita su propio stop-loss. La tarifa coincide con el monto invertido en ese plan.',
    [flowKeys.protectNoInvestments]:
      'Necesitas al menos una inversión activa antes de comprar protección stop-loss.',
    [flowKeys.protectNoInvestmentsCta]: 'Ver inversiones',
    [flowKeys.protectAllProtected]:
      'Todos tus planes activos ya tienen protección stop-loss.',
    [flowKeys.protectLoading]: 'Cargando planes que puedes proteger…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Invertido {{principal}} · Actual {{current}}',
    [flowKeys.protectFeeLine]: 'Tarifa de protección (monto del plan): {{amount}}',
    [flowKeys.protectPremiumLine]: 'Prima sobre el valor protegido: {{n}}%',
    [flowKeys.protectCta]: 'Proteger este plan',
    [flowKeys.protectCtaChoosePlan]: 'Proteger un plan…',
    [flowKeys.protectModalTitle]: 'Proteger {{name}}',
    [flowKeys.protectSelectPlanHint]: '¿A qué plan debe aplicarse este stop-loss?',
    [flowKeys.protectConfirm]: 'Comprar y aplicar protección',
    [flowKeys.protectProcessing]: 'Procesando…',
    [flowKeys.protectToastOk]: 'Stop-loss aplicado a tu plan',
    [flowKeys.protectChoosePackage]: 'Elige un paquete stop-loss',
    [flowKeys.protectPlanTierLine]: 'Nivel de inversión: {{tier}}',
    [flowKeys.protectForTierOnly]: 'Solo para inversiones {{tier}}',
    [flowKeys.protectRecommendedTier]: 'Recomendado para planes nivel {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'Seleccionar paquete',
    [flowKeys.protectPackageSelected]: 'Seleccionado',
    [flowKeys.protectSetLimitsFor]: 'Definir límites de protección para {{name}}',
    [flowKeys.tablePlanProtection]: 'Protección {{name}}',
    [flowKeys.explainerBadgeMulti]: 'Una protección por plan activo',
    [flowKeys.howStep1Title]: 'Elige tu plan',
    [flowKeys.howStep1Desc]:
      'Elige qué inversión activa proteger — un stop-loss por plan, tarifa igual al monto del plan',
    [flowKeys.investmentsStopLossApplied]: 'Stop-loss aplicado',
  },
  de: {
    [flowKeys.emptyDescNoSlots]:
      'Schützen Sie unten eine aktive Investition (ein Stop-Loss pro Plan).',
    [flowKeys.purchaseToastOk]: 'Stop-Loss-Schutz aktiviert',
    [flowKeys.purchasePlanLine]: 'Plan: {{name}}',
    [flowKeys.protectTitle]: 'Investition schützen',
    [flowKeys.protectSubtitle]:
      'Jeder aktive Plan braucht eigenen Stop-Loss. Die Gebühr entspricht dem investierten Betrag dieses Plans.',
    [flowKeys.protectNoInvestments]:
      'Sie brauchen mindestens eine aktive Investition, bevor Sie Stop-Loss kaufen können.',
    [flowKeys.protectNoInvestmentsCta]: 'Investitionen anzeigen',
    [flowKeys.protectAllProtected]:
      'Alle Ihre aktiven Pläne haben bereits Stop-Loss-Schutz.',
    [flowKeys.protectLoading]: 'Pläne zum Schützen werden geladen…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Investiert {{principal}} · Aktuell {{current}}',
    [flowKeys.protectFeeLine]: 'Schutzgebühr (Planbetrag): {{amount}}',
    [flowKeys.protectPremiumLine]: 'Prämie auf geschützten Wert: {{n}}%',
    [flowKeys.protectCta]: 'Diesen Plan schützen',
    [flowKeys.protectCtaChoosePlan]: 'Plan schützen…',
    [flowKeys.protectModalTitle]: '{{name}} schützen',
    [flowKeys.protectSelectPlanHint]: 'Für welchen Plan soll dieser Stop-Loss gelten?',
    [flowKeys.protectConfirm]: 'Kaufen & Schutz anwenden',
    [flowKeys.protectProcessing]: 'Wird verarbeitet…',
    [flowKeys.protectToastOk]: 'Stop-Loss auf Ihren Plan angewendet',
    [flowKeys.protectChoosePackage]: 'Stop-Loss-Paket wählen',
    [flowKeys.protectPlanTierLine]: 'Investitionsstufe: {{tier}}',
    [flowKeys.protectForTierOnly]: 'Nur für {{tier}}-Investitionen',
    [flowKeys.protectRecommendedTier]: 'Empfohlen für {{tier}}-Pläne',
    [flowKeys.protectContinueWithPackage]: 'Paket auswählen',
    [flowKeys.protectPackageSelected]: 'Ausgewählt',
    [flowKeys.protectSetLimitsFor]: 'Schutzlimits für {{name}} festlegen',
    [flowKeys.tablePlanProtection]: '{{name}}-Schutz',
    [flowKeys.explainerBadgeMulti]: 'Ein Schutz pro aktivem Plan',
    [flowKeys.howStep1Title]: 'Plan wählen',
    [flowKeys.howStep1Desc]:
      'Aktive Investition wählen — ein Stop-Loss pro Plan, Gebühr = Planbetrag',
    [flowKeys.investmentsStopLossApplied]: 'Stop-Loss aktiv',
  },
  fr: {
    [flowKeys.emptyDescNoSlots]:
      'Protégez un investissement actif ci-dessous (un stop-loss par plan).',
    [flowKeys.purchaseToastOk]: 'Protection stop-loss activée',
    [flowKeys.purchasePlanLine]: 'Plan : {{name}}',
    [flowKeys.protectTitle]: 'Protéger un investissement',
    [flowKeys.protectSubtitle]:
      'Chaque plan actif a besoin de son propre stop-loss. Les frais correspondent au montant investi sur ce plan.',
    [flowKeys.protectNoInvestments]:
      'Vous devez avoir au moins un investissement actif avant d’acheter une protection stop-loss.',
    [flowKeys.protectNoInvestmentsCta]: 'Voir les investissements',
    [flowKeys.protectAllProtected]:
      'Tous vos plans actifs ont déjà une protection stop-loss.',
    [flowKeys.protectLoading]: 'Chargement des plans à protéger…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Investi {{principal}} · Actuel {{current}}',
    [flowKeys.protectFeeLine]: 'Frais de protection (montant du plan) : {{amount}}',
    [flowKeys.protectPremiumLine]: 'Prime sur la valeur protégée : {{n}}%',
    [flowKeys.protectCta]: 'Protéger ce plan',
    [flowKeys.protectCtaChoosePlan]: 'Protéger un plan…',
    [flowKeys.protectModalTitle]: 'Protéger {{name}}',
    [flowKeys.protectSelectPlanHint]: 'À quel plan appliquer ce stop-loss ?',
    [flowKeys.protectConfirm]: 'Acheter et appliquer la protection',
    [flowKeys.protectProcessing]: 'Traitement…',
    [flowKeys.protectToastOk]: 'Stop-loss appliqué à votre plan',
    [flowKeys.protectChoosePackage]: 'Choisir un forfait stop-loss',
    [flowKeys.protectPlanTierLine]: 'Niveau d’investissement : {{tier}}',
    [flowKeys.protectForTierOnly]: 'Réservé aux investissements {{tier}}',
    [flowKeys.protectRecommendedTier]: 'Recommandé pour les plans {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'Sélectionner le forfait',
    [flowKeys.protectPackageSelected]: 'Sélectionné',
    [flowKeys.protectSetLimitsFor]: 'Définir les limites de protection pour {{name}}',
    [flowKeys.tablePlanProtection]: 'Protection {{name}}',
    [flowKeys.explainerBadgeMulti]: 'Une protection par plan actif',
    [flowKeys.howStep1Title]: 'Choisissez votre plan',
    [flowKeys.howStep1Desc]:
      'Choisissez l’investissement actif à protéger — un stop-loss par plan, frais = montant du plan',
    [flowKeys.investmentsStopLossApplied]: 'Stop-loss appliqué',
  },
  ar: {
    [flowKeys.emptyDescNoSlots]:
      'احمِ استثمارًا نشطًا أدناه (وقف خسارة واحد لكل خطة).',
    [flowKeys.purchaseToastOk]: 'تم تطبيق حماية وقف الخسارة',
    [flowKeys.purchasePlanLine]: 'الخطة: {{name}}',
    [flowKeys.protectTitle]: 'حماية استثمار',
    [flowKeys.protectSubtitle]:
      'كل خطة نشطة تحتاج وقف خسارة خاصًا. الرسوم تساوي المبلغ المستثمر في تلك الخطة.',
    [flowKeys.protectNoInvestments]:
      'تحتاج استثمارًا نشطًا واحدًا على الأقل قبل شراء حماية وقف الخسارة.',
    [flowKeys.protectNoInvestmentsCta]: 'عرض الاستثمارات',
    [flowKeys.protectAllProtected]: 'جميع خططك النشطة لديها بالفعل حماية وقف خسارة.',
    [flowKeys.protectLoading]: 'جاري تحميل الخطط التي يمكن حمايتها…',
    [flowKeys.protectPlanMeta]: '{{tier}} · مستثمر {{principal}} · الحالي {{current}}',
    [flowKeys.protectFeeLine]: 'رسوم الحماية (مبلغ الخطة): {{amount}}',
    [flowKeys.protectPremiumLine]: 'قسط على القيمة المحمية: {{n}}%',
    [flowKeys.protectCta]: 'حماية هذه الخطة',
    [flowKeys.protectCtaChoosePlan]: 'حماية خطة…',
    [flowKeys.protectModalTitle]: 'حماية {{name}}',
    [flowKeys.protectSelectPlanHint]: 'أي خطة تريد تطبيق وقف الخسارة عليها؟',
    [flowKeys.protectConfirm]: 'شراء وتطبيق الحماية',
    [flowKeys.protectProcessing]: 'جاري المعالجة…',
    [flowKeys.protectToastOk]: 'تم تطبيق وقف الخسارة على خطتك',
    [flowKeys.protectChoosePackage]: 'اختر باقة وقف الخسارة',
    [flowKeys.protectPlanTierLine]: 'مستوى الاستثمار: {{tier}}',
    [flowKeys.protectForTierOnly]: 'لاستثمارات {{tier}} فقط',
    [flowKeys.protectRecommendedTier]: 'موصى به لخطط مستوى {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'اختر الباقة',
    [flowKeys.protectPackageSelected]: 'محدد',
    [flowKeys.protectSetLimitsFor]: 'تعيين حدود الحماية لـ {{name}}',
    [flowKeys.tablePlanProtection]: 'حماية {{name}}',
    [flowKeys.explainerBadgeMulti]: 'حماية واحدة لكل خطة نشطة',
    [flowKeys.howStep1Title]: 'اختر خطتك',
    [flowKeys.howStep1Desc]:
      'اختر الاستثمار النشط للحماية — وقف خسارة لكل خطة، الرسوم = مبلغ الخطة',
    [flowKeys.investmentsStopLossApplied]: 'وقف خسارة مُطبَّق',
  },
  ja: {
    [flowKeys.emptyDescNoSlots]:
      '下の運用中投資を保護してください（プランごとに1つのストップロス）。',
    [flowKeys.purchaseToastOk]: 'ストップロス保護を適用しました',
    [flowKeys.purchasePlanLine]: 'プラン: {{name}}',
    [flowKeys.protectTitle]: '投資を保護',
    [flowKeys.protectSubtitle]:
      '運用中のプランごとにストップロスが必要です。保護料はそのプランの投資額と同じです。',
    [flowKeys.protectNoInvestments]:
      'ストップロスを購入するには、少なくとも1件の運用中投資が必要です。',
    [flowKeys.protectNoInvestmentsCta]: '投資を見る',
    [flowKeys.protectAllProtected]: '運用中のプランはすべてストップロス保護済みです。',
    [flowKeys.protectLoading]: '保護できるプランを読み込み中…',
    [flowKeys.protectPlanMeta]: '{{tier}} · 投資 {{principal}} · 現在 {{current}}',
    [flowKeys.protectFeeLine]: '保護料（プラン金額）: {{amount}}',
    [flowKeys.protectPremiumLine]: '保護価値に対するプレミアム: {{n}}%',
    [flowKeys.protectCta]: 'このプランを保護',
    [flowKeys.protectCtaChoosePlan]: 'プランを保護…',
    [flowKeys.protectModalTitle]: '{{name}} を保護',
    [flowKeys.protectSelectPlanHint]: 'どのプランにストップロスを適用しますか？',
    [flowKeys.protectConfirm]: '購入して保護を適用',
    [flowKeys.protectProcessing]: '処理中…',
    [flowKeys.protectToastOk]: 'プランにストップロスを適用しました',
    [flowKeys.protectChoosePackage]: 'ストップロスパッケージを選択',
    [flowKeys.protectPlanTierLine]: '投資ティア: {{tier}}',
    [flowKeys.protectForTierOnly]: '{{tier}} 投資のみ対象',
    [flowKeys.protectRecommendedTier]: '{{tier}} ティアのプランにおすすめ',
    [flowKeys.protectContinueWithPackage]: 'パッケージを選択',
    [flowKeys.protectPackageSelected]: '選択済み',
    [flowKeys.protectSetLimitsFor]: '{{name}} の保護上限を設定',
    [flowKeys.tablePlanProtection]: '{{name}} の保護',
    [flowKeys.explainerBadgeMulti]: '運用中プランごとに1つの保護',
    [flowKeys.howStep1Title]: 'プランを選択',
    [flowKeys.howStep1Desc]:
      '保護する運用中投資を選ぶ — プランごとに1つ、料金はプラン金額と同額',
    [flowKeys.investmentsStopLossApplied]: 'ストップロス適用済み',
  },
  ko: {
    [flowKeys.emptyDescNoSlots]:
      '아래에서 진행 중인 투자를 보호하세요(플랜당 스톱로스 1개).',
    [flowKeys.purchaseToastOk]: '스톱로스 보호가 적용되었습니다',
    [flowKeys.purchasePlanLine]: '플랜: {{name}}',
    [flowKeys.protectTitle]: '투자 보호',
    [flowKeys.protectSubtitle]:
      '각 진행 중인 플랜마다 스톱로스가 필요합니다. 보호 수수료는 해당 플랜의 투자 금액과 같습니다.',
    [flowKeys.protectNoInvestments]:
      '스톱로스를 구매하려면 최소 한 건의 진행 중인 투자가 필요합니다.',
    [flowKeys.protectNoInvestmentsCta]: '투자 보기',
    [flowKeys.protectAllProtected]: '진행 중인 모든 플랜에 이미 스톱로스가 적용되어 있습니다.',
    [flowKeys.protectLoading]: '보호 가능한 플랜 불러오는 중…',
    [flowKeys.protectPlanMeta]: '{{tier}} · 투자 {{principal}} · 현재 {{current}}',
    [flowKeys.protectFeeLine]: '보호 수수료(플랜 금액): {{amount}}',
    [flowKeys.protectPremiumLine]: '보호 가치에 대한 프리미엄: {{n}}%',
    [flowKeys.protectCta]: '이 플랜 보호',
    [flowKeys.protectCtaChoosePlan]: '플랜 보호…',
    [flowKeys.protectModalTitle]: '{{name}} 보호',
    [flowKeys.protectSelectPlanHint]: '어떤 플랜에 스톱로스를 적용할까요?',
    [flowKeys.protectConfirm]: '구매 및 보호 적용',
    [flowKeys.protectProcessing]: '처리 중…',
    [flowKeys.protectToastOk]: '플랜에 스톱로스가 적용되었습니다',
    [flowKeys.protectChoosePackage]: '스톱로스 패키지 선택',
    [flowKeys.protectPlanTierLine]: '투자 등급: {{tier}}',
    [flowKeys.protectForTierOnly]: '{{tier}} 투자만 해당',
    [flowKeys.protectRecommendedTier]: '{{tier}} 등급 플랜에 권장',
    [flowKeys.protectContinueWithPackage]: '패키지 선택',
    [flowKeys.protectPackageSelected]: '선택됨',
    [flowKeys.protectSetLimitsFor]: '{{name}} 보호 한도 설정',
    [flowKeys.tablePlanProtection]: '{{name}} 보호',
    [flowKeys.explainerBadgeMulti]: '진행 중 플랜당 보호 1개',
    [flowKeys.howStep1Title]: '플랜 선택',
    [flowKeys.howStep1Desc]:
      '보호할 진행 중 투자 선택 — 플랜당 스톱로스 1개, 수수료 = 플랜 금액',
    [flowKeys.investmentsStopLossApplied]: '스톱로스 적용됨',
  },
  'zh-CN': {
    [flowKeys.emptyDescNoSlots]: '在下方保护一项进行中的投资（每个方案一个止损）。',
    [flowKeys.purchaseToastOk]: '止损保护已应用',
    [flowKeys.purchasePlanLine]: '方案：{{name}}',
    [flowKeys.protectTitle]: '保护投资',
    [flowKeys.protectSubtitle]:
      '每个进行中的方案都需要各自的止损。保护费用等于该方案的投资金额。',
    [flowKeys.protectNoInvestments]: '购买止损保护前，您需要至少一项进行中的投资。',
    [flowKeys.protectNoInvestmentsCta]: '查看投资',
    [flowKeys.protectAllProtected]: '您所有进行中的方案均已启用止损保护。',
    [flowKeys.protectLoading]: '正在加载可保护的方案…',
    [flowKeys.protectPlanMeta]: '{{tier}} · 已投 {{principal}} · 当前 {{current}}',
    [flowKeys.protectFeeLine]: '保护费（方案金额）：{{amount}}',
    [flowKeys.protectPremiumLine]: '受保护价值的保费：{{n}}%',
    [flowKeys.protectCta]: '保护此方案',
    [flowKeys.protectCtaChoosePlan]: '保护方案…',
    [flowKeys.protectModalTitle]: '保护 {{name}}',
    [flowKeys.protectSelectPlanHint]: '止损应应用于哪个方案？',
    [flowKeys.protectConfirm]: '购买并应用保护',
    [flowKeys.protectProcessing]: '处理中…',
    [flowKeys.protectToastOk]: '止损已应用于您的方案',
    [flowKeys.protectChoosePackage]: '选择止损套餐',
    [flowKeys.protectPlanTierLine]: '投资等级：{{tier}}',
    [flowKeys.protectForTierOnly]: '仅适用于 {{tier}} 投资',
    [flowKeys.protectRecommendedTier]: '推荐用于 {{tier}} 层级计划',
    [flowKeys.protectContinueWithPackage]: '选择套餐',
    [flowKeys.protectPackageSelected]: '已选择',
    [flowKeys.protectSetLimitsFor]: '为 {{name}} 设置保护限额',
    [flowKeys.tablePlanProtection]: '{{name}} 保护',
    [flowKeys.explainerBadgeMulti]: '每个进行中方案一项保护',
    [flowKeys.howStep1Title]: '选择方案',
    [flowKeys.howStep1Desc]:
      '选择要保护的进行中投资 — 每方案一个止损，费用等于方案金额',
    [flowKeys.investmentsStopLossApplied]: '已应用止损',
  },
  pt: {
    [flowKeys.emptyDescNoSlots]:
      'Proteja um investimento ativo abaixo (um stop-loss por plano).',
    [flowKeys.purchaseToastOk]: 'Proteção stop-loss aplicada',
    [flowKeys.purchasePlanLine]: 'Plano: {{name}}',
    [flowKeys.protectTitle]: 'Proteger um investimento',
    [flowKeys.protectSubtitle]:
      'Cada plano ativo precisa do seu stop-loss. A taxa corresponde ao valor investido nesse plano.',
    [flowKeys.protectNoInvestments]:
      'Precisa de pelo menos um investimento ativo antes de comprar proteção stop-loss.',
    [flowKeys.protectNoInvestmentsCta]: 'Ver investimentos',
    [flowKeys.protectAllProtected]:
      'Todos os seus planos ativos já têm proteção stop-loss.',
    [flowKeys.protectLoading]: 'A carregar planos que pode proteger…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Investido {{principal}} · Atual {{current}}',
    [flowKeys.protectFeeLine]: 'Taxa de proteção (valor do plano): {{amount}}',
    [flowKeys.protectPremiumLine]: 'Prémio sobre o valor protegido: {{n}}%',
    [flowKeys.protectCta]: 'Proteger este plano',
    [flowKeys.protectCtaChoosePlan]: 'Proteger um plano…',
    [flowKeys.protectModalTitle]: 'Proteger {{name}}',
    [flowKeys.protectSelectPlanHint]: 'A que plano deve aplicar-se este stop-loss?',
    [flowKeys.protectConfirm]: 'Comprar e aplicar proteção',
    [flowKeys.protectProcessing]: 'A processar…',
    [flowKeys.protectToastOk]: 'Stop-loss aplicado ao seu plano',
    [flowKeys.protectChoosePackage]: 'Escolha um pacote stop-loss',
    [flowKeys.protectPlanTierLine]: 'Nível do investimento: {{tier}}',
    [flowKeys.protectForTierOnly]: 'Apenas para investimentos {{tier}}',
    [flowKeys.protectRecommendedTier]: 'Recomendado para planos {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'Selecionar pacote',
    [flowKeys.protectPackageSelected]: 'Selecionado',
    [flowKeys.protectSetLimitsFor]: 'Definir limites de proteção para {{name}}',
    [flowKeys.tablePlanProtection]: 'Proteção {{name}}',
    [flowKeys.explainerBadgeMulti]: 'Uma proteção por plano ativo',
    [flowKeys.howStep1Title]: 'Escolha o plano',
    [flowKeys.howStep1Desc]:
      'Escolha o investimento ativo a proteger — um stop-loss por plano, taxa = valor do plano',
    [flowKeys.investmentsStopLossApplied]: 'Stop-loss aplicado',
  },
  it: {
    [flowKeys.emptyDescNoSlots]:
      'Proteggi un investimento attivo qui sotto (uno stop-loss per piano).',
    [flowKeys.purchaseToastOk]: 'Protezione stop-loss applicata',
    [flowKeys.purchasePlanLine]: 'Piano: {{name}}',
    [flowKeys.protectTitle]: 'Proteggi un investimento',
    [flowKeys.protectSubtitle]:
      'Ogni piano attivo ha bisogno del proprio stop-loss. La tariffa corrisponde all’importo investito su quel piano.',
    [flowKeys.protectNoInvestments]:
      'Serve almeno un investimento attivo prima di acquistare la protezione stop-loss.',
    [flowKeys.protectNoInvestmentsCta]: 'Vedi investimenti',
    [flowKeys.protectAllProtected]:
      'Tutti i tuoi piani attivi hanno già protezione stop-loss.',
    [flowKeys.protectLoading]: 'Caricamento piani da proteggere…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Investito {{principal}} · Attuale {{current}}',
    [flowKeys.protectFeeLine]: 'Tariffa protezione (importo piano): {{amount}}',
    [flowKeys.protectPremiumLine]: 'Premio sul valore protetto: {{n}}%',
    [flowKeys.protectCta]: 'Proteggi questo piano',
    [flowKeys.protectCtaChoosePlan]: 'Proteggi un piano…',
    [flowKeys.protectModalTitle]: 'Proteggi {{name}}',
    [flowKeys.protectSelectPlanHint]: 'A quale piano applicare questo stop-loss?',
    [flowKeys.protectConfirm]: 'Acquista e applica protezione',
    [flowKeys.protectProcessing]: 'Elaborazione…',
    [flowKeys.protectToastOk]: 'Stop-loss applicato al tuo piano',
    [flowKeys.protectChoosePackage]: 'Scegli un pacchetto stop-loss',
    [flowKeys.protectPlanTierLine]: 'Livello investimento: {{tier}}',
    [flowKeys.protectForTierOnly]: 'Solo per investimenti {{tier}}',
    [flowKeys.protectRecommendedTier]: 'Consigliato per piani {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'Seleziona pacchetto',
    [flowKeys.protectPackageSelected]: 'Selezionato',
    [flowKeys.protectSetLimitsFor]: 'Imposta limiti di protezione per {{name}}',
    [flowKeys.tablePlanProtection]: 'Protezione {{name}}',
    [flowKeys.explainerBadgeMulti]: 'Una protezione per piano attivo',
    [flowKeys.howStep1Title]: 'Scegli il piano',
    [flowKeys.howStep1Desc]:
      'Scegli quale investimento attivo proteggere — uno stop-loss per piano, tariffa = importo piano',
    [flowKeys.investmentsStopLossApplied]: 'Stop-loss applicato',
  },
  ru: {
    [flowKeys.emptyDescNoSlots]:
      'Защитите активную инвестицию ниже (один стоп-лосс на план).',
    [flowKeys.purchaseToastOk]: 'Защита стоп-лосс применена',
    [flowKeys.purchasePlanLine]: 'План: {{name}}',
    [flowKeys.protectTitle]: 'Защитить инвестицию',
    [flowKeys.protectSubtitle]:
      'Каждому активному плану нужен свой стоп-лосс. Плата равна сумме инвестиции в этот план.',
    [flowKeys.protectNoInvestments]:
      'Нужна хотя бы одна активная инвестиция, прежде чем покупать стоп-лосс.',
    [flowKeys.protectNoInvestmentsCta]: 'Смотреть инвестиции',
    [flowKeys.protectAllProtected]: 'На всех активных планах уже есть стоп-лосс.',
    [flowKeys.protectLoading]: 'Загрузка планов для защиты…',
    [flowKeys.protectPlanMeta]: '{{tier}} · Вложено {{principal}} · Сейчас {{current}}',
    [flowKeys.protectFeeLine]: 'Плата за защиту (сумма плана): {{amount}}',
    [flowKeys.protectPremiumLine]: 'Премия на защищённую стоимость: {{n}}%',
    [flowKeys.protectCta]: 'Защитить этот план',
    [flowKeys.protectCtaChoosePlan]: 'Защитить план…',
    [flowKeys.protectModalTitle]: 'Защитить {{name}}',
    [flowKeys.protectSelectPlanHint]: 'К какому плану применить стоп-лосс?',
    [flowKeys.protectConfirm]: 'Купить и применить защиту',
    [flowKeys.protectProcessing]: 'Обработка…',
    [flowKeys.protectToastOk]: 'Стоп-лосс применён к вашему плану',
    [flowKeys.protectChoosePackage]: 'Выберите пакет стоп-лосс',
    [flowKeys.protectPlanTierLine]: 'Уровень инвестиции: {{tier}}',
    [flowKeys.protectForTierOnly]: 'Только для инвестиций {{tier}}',
    [flowKeys.protectRecommendedTier]: 'Рекомендуется для планов уровня {{tier}}',
    [flowKeys.protectContinueWithPackage]: 'Выбрать пакет',
    [flowKeys.protectPackageSelected]: 'Выбрано',
    [flowKeys.protectSetLimitsFor]: 'Задать лимиты защиты для {{name}}',
    [flowKeys.tablePlanProtection]: 'Защита {{name}}',
    [flowKeys.explainerBadgeMulti]: 'Одна защита на активный план',
    [flowKeys.howStep1Title]: 'Выберите план',
    [flowKeys.howStep1Desc]:
      'Выберите активную инвестицию — один стоп-лосс на план, плата = сумма плана',
    [flowKeys.investmentsStopLossApplied]: 'Стоп-лосс применён',
  },
  bn: {
    [flowKeys.emptyDescNoSlots]:
      'নিচে একটি সক্রিয় বিনিয়োগ সুরক্ষিত করুন (প্রতি পরিকল্পনায় একটি স্টপ-লস)।',
    [flowKeys.purchaseToastOk]: 'স্টপ-লস সুরক্ষা প্রয়োগ হয়েছে',
    [flowKeys.purchasePlanLine]: 'পরিকল্পনা: {{name}}',
    [flowKeys.protectTitle]: 'বিনিয়োগ সুরক্ষা',
    [flowKeys.protectSubtitle]:
      'প্রতিটি সক্রিয় পরিকল্পনার জন্য আলাদা স্টপ-লস দরকার। ফি সেই পরিকল্পনার বিনিয়োগের পরিমাণের সমান।',
    [flowKeys.protectNoInvestments]:
      'স্টপ-লস কেনার আগে কমপক্ষে একটি সক্রিয় বিনিয়োগ থাকতে হবে।',
    [flowKeys.protectNoInvestmentsCta]: 'বিনিয়োগ দেখুন',
    [flowKeys.protectAllProtected]: 'আপনার সব সক্রিয় পরিকল্পনায় ইতিমধ্যে স্টপ-লস আছে।',
    [flowKeys.protectLoading]: 'সুরক্ষা যোগ্য পরিকল্পনা লোড হচ্ছে…',
    [flowKeys.protectPlanMeta]: '{{tier}} · বিনিয়োগ {{principal}} · বর্তমান {{current}}',
    [flowKeys.protectFeeLine]: 'সুরক্ষা ফি (পরিকল্পনার পরিমাণ): {{amount}}',
    [flowKeys.protectPremiumLine]: 'সুরক্ষিত মূল্যের প্রিমিয়াম: {{n}}%',
    [flowKeys.protectCta]: 'এই পরিকল্পনা সুরক্ষা',
    [flowKeys.protectCtaChoosePlan]: 'পরিকল্পনা সুরক্ষা…',
    [flowKeys.protectModalTitle]: '{{name}} সুরক্ষা',
    [flowKeys.protectSelectPlanHint]: 'কোন পরিকল্পনায় স্টপ-লস প্রয়োগ করবেন?',
    [flowKeys.protectConfirm]: 'কিনুন ও সুরক্ষা প্রয়োগ',
    [flowKeys.protectProcessing]: 'প্রক্রিয়াকরণ…',
    [flowKeys.protectToastOk]: 'আপনার পরিকল্পনায় স্টপ-লস প্রয়োগ হয়েছে',
    [flowKeys.protectChoosePackage]: 'স্টপ-লস প্যাকেজ বেছে নিন',
    [flowKeys.protectPlanTierLine]: 'বিনিয়োগ স্তর: {{tier}}',
    [flowKeys.protectForTierOnly]: 'শুধু {{tier}} বিনিয়োগের জন্য',
    [flowKeys.protectRecommendedTier]: '{{tier}} টিয়ার প্ল্যানের জন্য সুপারিশকৃত',
    [flowKeys.protectContinueWithPackage]: 'প্যাকেজ নির্বাচন',
    [flowKeys.protectPackageSelected]: 'নির্বাচিত',
    [flowKeys.protectSetLimitsFor]: '{{name}}-এর সুরক্ষা সীমা নির্ধারণ',
    [flowKeys.tablePlanProtection]: '{{name}} সুরক্ষা',
    [flowKeys.explainerBadgeMulti]: 'প্রতি সক্রিয় পরিকল্পনায় একটি সুরক্ষা',
    [flowKeys.howStep1Title]: 'পরিকল্পনা বেছে নিন',
    [flowKeys.howStep1Desc]:
      'কোন সক্রিয় বিনিয়োগ সুরক্ষা করবেন — প্রতি পরিকল্পনায় একটি স্টপ-লস, ফি = পরিকল্পনার পরিমাণ',
    [flowKeys.investmentsStopLossApplied]: 'স্টপ-লস প্রয়োগ হয়েছে',
  },
  hi: {
    [flowKeys.emptyDescNoSlots]:
      'नीचे एक सक्रिय निवेश की सुरक्षा करें (प्रति योजना एक स्टॉप-लॉस)।',
    [flowKeys.purchaseToastOk]: 'स्टॉप-लॉस सुरक्षा लागू',
    [flowKeys.purchasePlanLine]: 'योजना: {{name}}',
    [flowKeys.protectTitle]: 'निवेश सुरक्षित करें',
    [flowKeys.protectSubtitle]:
      'प्रत्येक सक्रिय योजना को अपना स्टॉप-लॉस चाहिए। शुल्क उस योजना की निवेश राशि के बराबर है।',
    [flowKeys.protectNoInvestments]:
      'स्टॉप-लॉस खरीदने से पहले कम से कम एक सक्रिय निवेश होना चाहिए।',
    [flowKeys.protectNoInvestmentsCta]: 'निवेश देखें',
    [flowKeys.protectAllProtected]: 'आपकी सभी सक्रिय योजनाओं पर पहले से स्टॉप-लॉस है।',
    [flowKeys.protectLoading]: 'सुरक्षा योग्य योजनाएँ लोड हो रही हैं…',
    [flowKeys.protectPlanMeta]: '{{tier}} · निवेश {{principal}} · वर्तमान {{current}}',
    [flowKeys.protectFeeLine]: 'सुरक्षा शुल्क (योजना राशि): {{amount}}',
    [flowKeys.protectPremiumLine]: 'सुरक्षित मूल्य पर प्रीमियम: {{n}}%',
    [flowKeys.protectCta]: 'इस योजना को सुरक्षित करें',
    [flowKeys.protectCtaChoosePlan]: 'योजना सुरक्षित करें…',
    [flowKeys.protectModalTitle]: '{{name}} सुरक्षित करें',
    [flowKeys.protectSelectPlanHint]: 'स्टॉप-लॉस किस योजना पर लगे?',
    [flowKeys.protectConfirm]: 'खरीदें और सुरक्षा लागू करें',
    [flowKeys.protectProcessing]: 'प्रक्रिया जारी…',
    [flowKeys.protectToastOk]: 'आपकी योजना पर स्टॉप-लॉस लागू',
    [flowKeys.protectChoosePackage]: 'स्टॉप-लॉस पैकेज चुनें',
    [flowKeys.protectPlanTierLine]: 'निवेश स्तर: {{tier}}',
    [flowKeys.protectForTierOnly]: 'केवल {{tier}} निवेश के लिए',
    [flowKeys.protectRecommendedTier]: '{{tier}} टियर योजनाओं के लिए अनुशंसित',
    [flowKeys.protectContinueWithPackage]: 'पैकेज चुनें',
    [flowKeys.protectPackageSelected]: 'चयनित',
    [flowKeys.protectSetLimitsFor]: '{{name}} के लिए सुरक्षा सीमा सेट करें',
    [flowKeys.tablePlanProtection]: '{{name}} सुरक्षा',
    [flowKeys.explainerBadgeMulti]: 'प्रति सक्रिय योजना एक सुरक्षा',
    [flowKeys.howStep1Title]: 'योजना चुनें',
    [flowKeys.howStep1Desc]:
      'कौन सा सक्रिय निवेश सुरक्षित करें — प्रति योजना एक स्टॉप-लॉस, शुल्क = योजना राशि',
    [flowKeys.investmentsStopLossApplied]: 'स्टॉप-लॉस लागू',
  },
};
